import { Inject, Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { GenericResponseTotalPortfolioAnalyticsResponseBody, WweInputTotalPortfolio } from '@blk/wealth-web-endpoint';
import { ModelsEntitlementService, PortfolioAnalysisErrors } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { ConfigService, SharedConfigKey } from '@ddt-web/config-layer';
import { AnalysisParams, ApiService, ApiSettings, ApiStoreConfiguration, AppStoreConfig, PortfolioAnalysisOptions, WwePortfolioAnalysisServiceParams, WweRcResolverService } from '@ddt-web/data-service';
import { ErrorObject } from '@ddt-web/error-handler';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as groupBy from 'lodash/groupBy';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, first, map, mergeMap, take } from 'rxjs/operators';

import { WealthWebApiParamUtil } from '../utils/wealth-web-api-param.util';
import { WealthWebApiService } from './wealth-web-api.service';
import { WealthWebDataPortfolioAnalysisService } from './wealth-web-data-portfolio-analysis.service';

/**
 * This service is used for analyzing multiple portfolios with different benchmarks
 */
@Injectable()
@UntilDestroy()
export class WealthWebDataMultiPortfolioAnalysisService extends WealthWebDataPortfolioAnalysisService {
	private readonly ALL_ANALYSIS_CONFIG_NAMES = ['risk', 'details', 'performance'];

	constructor(
		apiService: ApiService,
		storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) protected storeConfig: AppStoreConfig,
		contentService: ContentService,
		modelsEntitlementService: ModelsEntitlementService,
		protected wealthWebApiService: WealthWebApiService,
		protected configService: ConfigService,
		protected wweRcResolver: WweRcResolverService
	) {
		super(apiService, storeService, storeConfig, contentService, modelsEntitlementService, wealthWebApiService, configService, wweRcResolver);
	}

	/**
	 * @description loadDefaultStoreData: performs the appropriate api calls and updates the corresponding stores.
	 * @param {apiSettings} apiSettings: api specific settings: rcs, config, and portfolioAnalysisParams.
	 * @param {AnalysisParams} analysisParams: the params used to build the api call.
	 */
	public loadDefaultStoreData(apiSettings: ApiSettings, analysisParams: any): void {
		const analysisSubs = apiSettings.configs.flatMap(config => {
			const apiSetting = { config, rcs: this.generateRcs([config]), portfolioAnalysisParams: null };
			return analysisParams.analysisParamsArray.map(analysisParam => {
				const useAladdinId = this.configService.get(SharedConfigKey.AWE_USE_ALADDINID, false);
				const portfolios = WealthWebApiParamUtil.createPortfolioParams(analysisParam?.analysisParamsArray, useAladdinId);
				const benchmark = WealthWebApiParamUtil.createBenchmarkParam(analysisParam, useAladdinId);
				if (this.isValidAnalysisData(portfolios)) {
					return this.wweRcResolver.getRcForUserType(apiSetting.config.name)
						.pipe(
							untilDestroyed(this),
							map((rc: string) => ({
								...apiSetting,
								wwePortfolioAnalysisParams: {
									body: {
										portfolios, benchmark, simulatedPerformanceUserInputs: analysisParam?.simulatedPerformanceUserInputs
									} as WweInputTotalPortfolio,
									portfolios: analysisParam.analysisParamsArray,
									rc
								} as WwePortfolioAnalysisServiceParams
							})),
							mergeMap((settings: ApiSettings) => {
								return this.dispatchFetchAwaPortfolioAnalysisApiCall(settings)
									.pipe(
										catchError(error => of(error)),
										map((response => [settings, response]))
									);
							})
						);
				}
				else {
					// inserting error codes for invalid portfolios
					this.storeService.upsertMany(
						apiSetting.config,
						portfolios.map(portfolio => ({ id: portfolio.id, error: { errorCode: PortfolioAnalysisErrors.PARTIAL_ERROR } }))
					);
				}
			});
		});

		// filtering out invalid subscriptions
		forkJoin(analysisSubs.filter(sub => !!sub)).pipe(take(1)).subscribe(res => {
			if (res?.length) {
				const analysisGrps: { [key: string]: any[] } = groupBy(res, data => data[0].config.name);
				let errorObject: { [key: string]: any[] } = {};
				Object.values(analysisGrps).forEach(grp => {
					const resultList = [];
					grp.forEach(data => {
						if (data[1]?.success) {
							resultList.push(...data[1].resultList);
						} else {
							const portfolioId = data[0].wwePortfolioAnalysisParams.portfolios[0].uniqueId;
							const portfolioErrors = errorObject?.[portfolioId] ?? [];
							portfolioErrors.push(data[1]);
							errorObject = { [portfolioId]: portfolioErrors, ...errorObject };
						}
					});
					const settings = grp[0][0];
					if (resultList.length) {
						this.processSuccessfulAnalysis(resultList, settings);
						if (settings.config.name === 'performance') {
							this.checkPerformanceDate(resultList);
						}
					}
				});

				const failedPortfolios = Object.entries(errorObject)?.filter(error => {
					const errorConfigNames = error[1].map(error => error.configName);
					return this.ALL_ANALYSIS_CONFIG_NAMES.every(name => errorConfigNames.includes(name));
				}).map(error => error[0]);

				if (failedPortfolios?.length) {
					apiSettings.configs.forEach(
						config => this.storeService.upsertMany(config, failedPortfolios.map(id => ({ id, error: { errorCode: PortfolioAnalysisErrors.PARTIAL_ERROR } })))
					);
				}

				if (Object.entries(errorObject)?.length > failedPortfolios.length) {
					Object.entries(errorObject).filter(errors => !failedPortfolios.includes(errors[0])).forEach(errors => {
						errors[1].forEach(error => this.storeService.upsertOne(this.storeConfig[error.configName], { id: errors[0], error }));
					});
				}
			}
		});
	}

	protected dispatchFetchAwaPortfolioAnalysisApiCall(apiSettings: ApiSettings): Observable<GenericResponseTotalPortfolioAnalyticsResponseBody> {
		// Create the observable which handles critical errors
		return this.wealthWebApiService.fetchPortfolioAnalysis(apiSettings.wwePortfolioAnalysisParams).pipe(
			first(),
			catchError((error) => {
				Object.assign(error, { 'portfolioUniqueId': apiSettings?.wwePortfolioAnalysisParams?.portfolios[0]?.uniqueId });
				throw new ErrorObject(error, apiSettings.config.name);
			})
		);
	}

	protected dispatchAllDataLoaders(
		configs: Array<ApiStoreConfiguration>,
		generalAnalysisParams: AnalysisParams,
		performanceAnalysisParams?: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		const apiSettings: ApiSettings = {
			portfolioAnalysisParams: null,
			configs: configs
		};
		configs.forEach(config => this.storeService.fetch(config));
		this.dispatchDataLoaders(apiSettings, generalAnalysisParams, performanceAnalysisParams, portfolioAnalysisOptions);
	}
}
